// Typed loader for the fifteen failure modes.
//
// reference/dispute-taxonomy.json is imported and never copied. Its evidentiary
// spine — tiers, detection signals, ground truth, the audits each mode is drawn
// from — is read-only: a second divergent copy of it would be exactly the
// version-drift failure FM-04 exists to catch.
//
// The redesign adds six fields to the same file rather than to a sidecar:
// planArea, plainTitle, plainSignal, cannotCatch, comment_template and
// genericComment. They are how a mode is named and phrased to a reviewer, not
// what it rests on, so they change nothing about the spine. They live here
// because a mode's plain title and its detection signal drifting apart is the
// same failure a sidecar was meant to avoid. See docs/redesign/ARCHITECTURE.md.

import taxonomyJson from '../../reference/dispute-taxonomy.json';
import type { FailureModeId, PlanAreaId, Tier } from '../types';
import type { EvidenceCaseId } from './evidence-cases';

export interface FailureMode {
  id: FailureModeId;
  name: string;
  tier: Tier;
  rrp_category: string;
  plain_description: string;
  where_it_appears: string;
  detection_signal: string[];
  /**
   * The same detection signal in a sentence a reviewer can read, for /checks.
   * The signals themselves stay as they are — they are what the engine matches
   * on and what a finding states as its basis — and this says what they come
   * to, without replacing them.
   */
  plainSignal: string;
  /**
   * What this check cannot catch. Every check has a boundary, and a reviewer
   * deciding how much weight to give a clean result needs it stated rather
   * than inferred from silence.
   */
  cannotCatch: string;
  why_it_produces_dispute: string;
  ground_truth: string;
  note?: string;
  /** Which part of the plan this mode is about. Findings are grouped by it. */
  planArea: PlanAreaId;
  /** The plain-language title. The primary label; `name` is engine voice. */
  plainTitle: string;
  /**
   * The line of context under a finding's claim, where the plain description's
   * own first sentence would carry jargon a reviewer should not have to know.
   * Optional: most modes read plainly enough already, and inventing a second
   * sentence for those would be copy nobody specified.
   */
  plainContext?: string;
  /**
   * The draft review comment, with {token} placeholders the engine fills from
   * the finding's receipt. See src/engine/comment/commentDraft.ts.
   */
  comment_template: string;
  /**
   * The fallback used when the template cannot be filled, so a placeholder
   * token — or a figure belonging to another document — can never reach the
   * user. Carries no figures of its own.
   */
  genericComment: string;
}

export interface Taxonomy {
  name: string;
  version: string;
  built_from: string[];
  scoring_note: string;
  failure_modes: FailureMode[];
}

export const TAXONOMY: Taxonomy = taxonomyJson as Taxonomy;

const BY_ID = new Map<FailureModeId, FailureMode>(
  TAXONOMY.failure_modes.map((fm) => [fm.id, fm]),
);

export function getFailureMode(id: FailureModeId): FailureMode {
  const fm = BY_ID.get(id);
  if (!fm) throw new Error(`Unknown failure mode ${id}`);
  return fm;
}

/** Tier comes from the taxonomy, never from a check's own judgement. */
export function tierOf(id: FailureModeId): Tier {
  return getFailureMode(id).tier;
}

/** The plain-language title, which is what a reviewer is shown. */
export function plainTitleOf(id: FailureModeId): string {
  return getFailureMode(id).plainTitle;
}

/** Which part of the plan a mode belongs to. Findings are grouped by this. */
export function planAreaOf(id: FailureModeId): PlanAreaId {
  return getFailureMode(id).planArea;
}

/**
 * The detection signal a check matched, quoted from the taxonomy so a flag's
 * stated basis is the published one rather than a paraphrase.
 */
export function detectionSignal(id: FailureModeId, index: number): string {
  const signals = getFailureMode(id).detection_signal;
  return signals[index] ?? signals[0] ?? '';
}

/**
 * Which audit cases document each failure mode, read off the taxonomy's
 * ground_truth field and mapped to the case ids in evidence-cases.ts.
 *
 * FM-15 is deliberately empty: its ground truth is "verified by recomputation",
 * and no federal audit names arithmetic non-reconciliation as a cause. A flag
 * with no case says so rather than borrowing one.
 */
const EVIDENCE_FOR_FM: Record<FailureModeId, EvidenceCaseId[]> = {
  'FM-01': ['VA-2023', 'FEMP-MV-5'],
  'FM-02': ['VA-2023', 'GSA-2020', 'GSA-2025', 'FEMP-AUDIT-2024'],
  'FM-03': ['GSA-2025'],
  'FM-04': ['VA-2023', 'FEMP-AUDIT-2024'],
  'FM-05': ['GSA-2020'],
  'FM-06': ['GSA-2020'],
  'FM-07': ['GSA-2020'],
  'FM-08': ['VA-2023', 'FEMP-MV-5'],
  'FM-09': ['FEMP-MV-5'],
  'FM-10': ['FEMP-MV-5'],
  'FM-11': ['GSA-2025', 'FEMP-AUDIT-2024'],
  'FM-12': ['GSA-2020'],
  'FM-13': ['FEMP-MV-5'],
  'FM-14': ['FEMP-MV-5'],
  'FM-15': [],
};

export function evidenceFor(id: FailureModeId): string[] {
  return EVIDENCE_FOR_FM[id] ?? [];
}
